import { useState, useEffect, useRef } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { storage, db } from "../../../firebase";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Drawer,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function FilePreview({ open, onClose, file, onToggleSummary, summaryOpen }) {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fallbackTried = useRef(false);

  useEffect(() => {
    fallbackTried.current = false;
    if (!file || !open) {
      setFileUrl(null);
      setError(null);
      return;
    }

    const fileType = file.fileType || "";

    if (fileType === "application/pdf") {
      // PDFs: Always fetch fresh URL and update Firestore
      fetchAndSyncUrl();
    } else if (fileType.startsWith("image/")) {
      // Images: Try Firestore URL, fallback if it fails
      if (file.downloadURL) {
        setFileUrl(file.downloadURL);
      } else if (file.storagePath) {
        fetchAndSyncUrl();
      }
    } else {
      // Other types: Try to use downloadURL or fetch new
      if (file.downloadURL) {
        setFileUrl(file.downloadURL);
      } else if (file.storagePath) {
        fetchAndSyncUrl();
      }
    }
    // eslint-disable-next-line
  }, [file, open]);

  // Always fetch a fresh download URL and sync to Firestore
  async function fetchAndSyncUrl() {
    setLoading(true);
    setError(null);
    try {
      const fileRef = ref(storage, file.storagePath);
      const url = await getDownloadURL(fileRef);
      setFileUrl(url);

      // --- Update Firestore with the new URL ---
      // Assumes your file doc path is known (adjust as needed!)
      // For example: users/{userId}/folders/{folderId}/subfolders/{subfolderId}/files/{fileId}
      if (file.userId && file.folderId && file.subfolderId && file.fileId) {
        const fileDocRef = doc(
          db,
          "users",
          file.userId,
          "folders",
          file.folderId,
          "subfolders",
          file.subfolderId,
          "files",
          file.fileId
        );
        await updateDoc(fileDocRef, { downloadURL: url });
      }
    } catch (err) {
      setError("Failed to load file.");
    } finally {
      setLoading(false);
    }
  }

  // Fallback for images if initial downloadURL fails
  function handleUrlError() {
    if (!fallbackTried.current && file.storagePath) {
      fallbackTried.current = true;
      fetchAndSyncUrl();
    } else {
      setError("Failed to load file.");
    }
  }

  const renderFileContent = () => {
    if (!fileUrl) return null;
    const fileType = file.fileType || "";

    if (fileType.startsWith("image/")) {
      return (
        <img
          src={fileUrl}
          alt={file.fileName}
          style={{ maxWidth: "100%", maxHeight: "80vh" }}
          onError={handleUrlError}
        />
      );
    } else if (fileType === "application/pdf") {
      return (
        <embed
          src={fileUrl}
          type="application/pdf"
          width="100%"
          height="100%"
          style={{
            minHeight: "70vh",
            maxHeight: "80vh",
            width: "100%",
            display: "block",
          }}
        />
      );
    } else {
      // For files that can't be previewed, just provide a download link
      return (
        <Typography>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            Download {file.fileName}
          </a>
        </Typography>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "#121212",
          color: "white",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, position: "relative" }}>
        {file ? file.fileName : "File Preview"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#ccc" }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
        {file?.summary && (
          <Button
            variant="outlined"
            onClick={onToggleSummary}
            sx={{
              position: "absolute",
              right: 60,
              top: 19,
              color: summaryOpen ? "#f44336" : "#90caf9", // red if summaryOpen, else blue
              borderColor: summaryOpen ? "#f44336" : "#90caf9",
              fontWeight: 700,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: summaryOpen ? "#2d1717" : "#23263b", // darker red if open
                borderColor: summaryOpen ? "#c62828" : "#1976d2",
              },
            }}
            size="small"
          >
            {summaryOpen ? "Close Summary" : "Check Summary"}
          </Button>
        )}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          textAlign: "center",
          height: "80vh",
          overflowY: "auto",
          p: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          renderFileContent()
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FilePreview;
